// --- helpers.ts — TypeScript conversion of helpers.js ---

import type { AccountRow, ResultRow, AuditDBRecord, TransactionRow } from './types';
import { t } from './translations';

declare const XLSX: any;

// --- EXCEL PROCESSOR (HIDDEN LOGIC) ---
export const ExcelProcessor = {
    cleanCellValue: (value: any): string => {
        if (value === null || value === undefined) return "";
        let sVal = String(value).trim();
        let cleanCandidate = sVal.replace(/'/g, "").replace(/\s/g, "");
        if (cleanCandidate === "") return sVal;

        let floatVal = Number(cleanCandidate);
        if (!isNaN(floatVal)) {
            return floatVal.toFixed(2).replace(".", ",");
        } else {
            return sVal;
        }
    },

    generateCSV: (data: any[][]): string => {
        const separator = ";";
        const lineTerminator = "\r\n";

        const rows = data.map(row => {
            if (!row) return "";
            return row.map(cell => {
                let processed = ExcelProcessor.cleanCellValue(cell);
                processed = processed.replace(/"/g, '""');
                return '"' + processed + '"';
            }).join(separator);
        });

        let result = rows.join(lineTerminator);
        result = result.replace(/[\r\n]+$/, "");
        return "\uFEFF" + result;
    },

    convertFile: (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e: any) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: 'array' });

                    if (workbook.SheetNames.length === 0) throw new Error("Excel pusty");
                    const firstSheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[firstSheetName];

                    let safeRange = worksheet['!ref'] || "A1:A1";
                    try {
                        const decoded = XLSX.utils.decode_range(safeRange);
                        if (decoded.s.c < 0 || decoded.s.r < 0) throw new Error("Ujemne indeksy");
                    } catch (err) {
                        safeRange = "A1:A1";
                    }

                    const jsonData = XLSX.utils.sheet_to_json(worksheet, {
                        header: 1,
                        raw: true,
                        defval: null,
                        range: safeRange
                    });

                    if (!jsonData || jsonData.length === 0) throw new Error("Brak danych w arkuszu");

                    let maxNonEmptyColIndex = -1;
                    jsonData.forEach((row: any) => {
                        if (row && row.length > 0) {
                            row.forEach((cell: any, index: number) => {
                                if (ExcelProcessor.cleanCellValue(cell) !== "") {
                                    if (index > maxNonEmptyColIndex) maxNonEmptyColIndex = index;
                                }
                            });
                        }
                    });

                    let finalData: any[][];
                    if (maxNonEmptyColIndex >= 0) {
                        finalData = jsonData.map((row: any) => {
                            const newRow: any[] = [];
                            for (let i = 0; i <= maxNonEmptyColIndex; i++) {
                                newRow[i] = (row && row[i] !== undefined) ? row[i] : null;
                            }
                            return newRow;
                        });
                    } else {
                        throw new Error("Pusty arkusz (same białe znaki)");
                    }

                    for (let i = finalData.length - 1; i >= 0; i--) {
                        const row = finalData[i];
                        const isEmpty = row.every((cell: any) => ExcelProcessor.cleanCellValue(cell) === "");
                        if (isEmpty) finalData.pop();
                        else break;
                    }

                    const csvContent = ExcelProcessor.generateCSV(finalData);
                    resolve(csvContent);

                } catch (err) {
                    reject(err);
                }
            };
            reader.onerror = (err) => reject(err);
            reader.readAsArrayBuffer(file);
        });
    }
};


// --- HELPERY (Istniejące) ---

export const MONTH_SHORT: string[] = ['Jan','Feb','Mar','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Des'];

// Okres audytu dla etykiet dat na kartach (11.10.2026, kontrakt karty). Najpierw jawny
// `config.period` z UI, potem zakres dat transakcji; bez obu etykieta jest pomijana,
// nigdy zgadywana.
export const resolvePeriod = (config: any, transactions?: Array<{ date?: string }> | null): { year: number; monthFrom: number; monthTo: number } | null => {
    const p = config?.period;
    const year = Number(p?.year), monthTo = Number(p?.monthTo), monthFrom = p?.monthFrom == null ? 1 : Number(p.monthFrom);
    if (p && Number.isInteger(year) && year > 0 && Number.isInteger(monthTo) && Number.isInteger(monthFrom)
        && monthFrom >= 1 && monthFrom <= monthTo && monthTo <= 12) {
        return { year, monthFrom, monthTo };
    }
    // Jawny, ale błędny okres (jakakolwiek z trzech wartości podana): nie zgaduj z transakcji.
    if (p && typeof p === 'object' && (p.year != null || p.monthFrom != null || p.monthTo != null)) return null;
    const months = (transactions || [])
        .map(tx => String(tx?.date || '').slice(0, 7))
        .filter(d => /^\d{4}-(0[1-9]|1[0-2])$/.test(d))
        .sort();
    if (months.length === 0) return null;
    const [y1, m1] = months[0].split('-').map(Number);
    const [y2, m2] = months[months.length - 1].split('-').map(Number);
    return { year: y2, monthFrom: y1 === y2 ? m1 : 1, monthTo: m2 };
};
export const periodEndDate = (p: { year: number; monthTo: number }): string => {
    const lastDay = new Date(Date.UTC(p.year, p.monthTo, 0)).getUTCDate();
    return `${String(lastDay).padStart(2, '0')}.${String(p.monthTo).padStart(2, '0')}.${p.year}`;
};
export const periodRange = (p: { year: number; monthFrom: number; monthTo: number }): string =>
    `${MONTH_SHORT[p.monthFrom - 1]}–${MONTH_SHORT[p.monthTo - 1]} ${p.year}`;
/** 'YYYY-MM-DD…' → 'DD.MM.YYYY'; inny kształt wraca bez zmian. */
export const isoToDotDate = (iso: any): string => {
    const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}.${m[2]}.${m[1]}` : String(iso || '');
};

export const Helpers = {
    monthLabel: (m: any): string => {
        const n = parseInt(m, 10);
        return (n >= 1 && n <= 12) ? MONTH_SHORT[n - 1] : m;
    },
    cleanText: (str: any): string => str ? str.toString().trim().replace(/\u00A0/g, ' ') : "",

    cleanAccountID: (str: any): string => {
        if (!str) return "";
        let s = str.toString().trim().replace(/\u00A0/g, '');
        return s.split(/[.,]/)[0];
    },

    cleanNumber: (str: any): number => {
        if (!str) return 0.0;
        if (typeof str === 'number') return isNaN(str) || !isFinite(str) ? 0.0 : str;
        let s = str.toString().trim().replace(/\s/g, '').replace(/\u00A0/g, '').replace(/'/g, '');
        // Handle "1.234,56" (dot=thousands, comma=decimal) — remove dots before comma swap
        if (s.includes(',') && s.includes('.')) s = s.replace(/\./g, '');
        s = s.replace(',', '.');
        if (s === '' || s === '-' || s === '.') return 0.0;
        if (s.endsWith('-') && !s.startsWith('-')) s = '-' + s.slice(0, -1);
        const val = parseFloat(s);
        return isNaN(val) || !isFinite(val) ? 0.0 : val;
    },

    translateMonth: (val: any): string => {
        if(!val) return "";
        const v = val.toLowerCase().replace('.', '').trim();
        const map: Record<string, string> = { 'jan': 'Jan', 'feb': 'Feb', 'mar': 'Mar', 'apr': 'Apr', 'mai': 'Mai', 'jun': 'Jun', 'jul': 'Jul', 'aug': 'Aug', 'sep': 'Sep', 'okt': 'Okt', 'nov': 'Nov', 'des': 'Des' };
        for (const key in map) if (v.includes(key)) return map[key];
        return val;
    },

    formatMoney: (amount: number | null | undefined): string => {
        if (amount === undefined || amount === null || isNaN(amount) || !isFinite(amount)) return '0,00';
        return amount.toLocaleString('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    generateCSV: (data: any[], type: string): string => {
        if (!data || data.length === 0) return '';

        let headers: string[] = [];
        let rows: any[][] = [];

        if (type === 'saldo') {
            headers = ['Kontonr', 'Kontonavn', 'IB', 'Endring', 'UB'];
            rows = data.map(r => [
                r.id,
                r.name,
                r.ib.toFixed(2).replace('.', ','),
                r.change.toFixed(2).replace('.', ','),
                r.ub.toFixed(2).replace('.', ',')
            ]);
        } else {
            const months = data[0] && data[0].months ? Object.keys(data[0].months) : [];
            headers = ['Kontonr', 'Kontonavn', ...months, 'Sum'];
            rows = data.map(r => [
                r.id,
                r.name,
                ...months.map(m => (r.months[m] || 0).toFixed(2).replace('.', ',')),
                r.sum.toFixed(2).replace('.', ',')
            ]);
        }

        const csvContent = [
            headers.join(';'),
            ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(';'))
        ].join('\r\n');

        return "\uFEFF" + csvContent;
    },

    downloadFile: (content: string, filename: string): void => {
        const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    },

    formatPeriodLabel: ({monthFrom, monthTo, year}: {monthFrom: any; monthTo: any; year: any}): string => {
        if (!year) return '';
        const mf = Number(monthFrom), mt = Number(monthTo);
        if (mf === mt) {
            // Single month — "Per DD.MM.YYYY" (last day of month)
            const lastDay = new Date(year, mt, 0).getDate();
            return `${t('period_per')} ${String(lastDay).padStart(2,'0')}.${String(mt).padStart(2,'0')}.${year}`;
        }
        // Range — "Jan–Jun 2025"
        return `${MONTH_SHORT[mf - 1]}\u2013${MONTH_SHORT[mt - 1]} ${year}`;
    }
};

export const ApiTransform = {
    transformSaldo: (raw: any[]): AccountRow[] => (Array.isArray(raw) ? raw : []).map(row => {
        const num = parseInt(String(row.n || row.accNum || '0'), 10);
        const ib = Number(row.ib ?? 0);
        const change = Number(row.ch ?? row.change ?? 0);
        return {
            id: String(row.n || row.accNum || ''),
            accNum: isNaN(num) ? 0 : num,
            name: row.name || row.accName || '',
            accName: row.name || row.accName || '',
            ib, change,
            ...(row.ibAvailable === false || row.ib == null || String(row.ib).trim() === ''
                || !['number', 'string'].includes(typeof row.ib) || !Number.isFinite(Number(row.ib))
                ? { ibAvailable: false } : {}),
            ub: Number(row.ub ?? (ib + change)),
            ...(row.months && typeof row.months === 'object' && !Array.isArray(row.months)
                && Object.values(row.months).every(v => typeof v === 'number' && Number.isFinite(v))
                ? { months: { ...row.months } } : {}),
        };
    }),
    transformResultat: (raw: any[], periodMonths?: string[]): ResultRow[] => (Array.isArray(raw) ? raw : []).map(row => {
        const num = parseInt(String(row.n || row.accNum || '0'), 10);
        const rawMonths = (row.months && typeof row.months === 'object') ? row.months : {};
        // Fill missing months with 0 so anomaly detection sees gaps
        const months: Record<string, number> = {};
        if (Array.isArray(periodMonths) && periodMonths.length > 0) {
            periodMonths.forEach((m: string) => { months[m] = Number(rawMonths[m] ?? 0); });
        } else {
            Object.assign(months, rawMonths);
        }
        const sum = Number(row.sum ?? Object.values(months).reduce((s: number, v: any) => s + Number(v || 0), 0));
        return {
            id: String(row.n || row.accNum || ''),
            accNum: isNaN(num) ? 0 : num,
            name: row.name || row.accName || '',
            accName: row.name || row.accName || '',
            months, sum,
            change: Number(row.ch ?? row.change ?? sum),
        };
    }),
    // previousYear z /api/generate-data: saldo za pełny rok (UB 31.12), resultat za ten sam
    // okres co audyt. Rachunek wyniku pełnego roku mamy tylko wtedy, gdy audyt obejmuje 1-12
    // albo gdy API przysłało go osobno; okresu częściowego nie wolno podać jako roku.
    transformPreviousYear: (raw: any, fallbackMonths?: string[]) => {
        if (!raw || typeof raw !== 'object') return null;
        const coversFullYear = Number(raw.startMonth) === 1 && Number(raw.endMonth) === 12;
        const fullYearRows = Array.isArray(raw.resultatFullYear) ? raw.resultatFullYear
            : (coversFullYear && Array.isArray(raw.resultat) ? raw.resultat : null);
        return {
            year: Number.isInteger(raw.year) && raw.year > 0 ? raw.year : null,
            saldo: ApiTransform.transformSaldo(raw.saldo),
            saldoEndMonth: Number.isInteger(raw.saldoEndMonth) && raw.saldoEndMonth >= 1 && raw.saldoEndMonth <= 12 ? raw.saldoEndMonth : null,
            resultat: ApiTransform.transformResultat(raw.resultat, raw.months || fallbackMonths),
            resultatFullYear: fullYearRows ? ApiTransform.transformResultat(fullYearRows) : null,
        };
    },
};

export const AuditDB = (() => {
    const DB_NAME = 'AuditBatchDB';
    const STORE = 'runs';
    const VERSION = 1;
    const open = (): Promise<IDBDatabase> => new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, VERSION);
        req.onupgradeneeded = () => { req.result.createObjectStore(STORE, { keyPath: 'runId' }); };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
    return {
        save: async (runId: string, data: Record<string, any>): Promise<void> => {
            const db = await open();
            return new Promise((resolve, reject) => {
                const tx = db.transaction(STORE, 'readwrite');
                tx.objectStore(STORE).put({ runId, ...data, savedAt: Date.now() });
                tx.oncomplete = () => resolve();
                tx.onerror = () => reject(tx.error);
            });
        },
        load: async (runId: string): Promise<AuditDBRecord | null> => {
            const db = await open();
            return new Promise((resolve, reject) => {
                const req = db.transaction(STORE, 'readonly').objectStore(STORE).get(runId);
                req.onsuccess = () => resolve(req.result || null);
                req.onerror = () => reject(req.error);
            });
        },
        listRuns: async (): Promise<AuditDBRecord[]> => {
            const db = await open();
            return new Promise((resolve, reject) => {
                const req = db.transaction(STORE, 'readonly').objectStore(STORE).getAll();
                req.onsuccess = () => resolve((req.result || []).sort((a: AuditDBRecord, b: AuditDBRecord) => b.savedAt - a.savedAt));
                req.onerror = () => reject(req.error);
            });
        },
        deleteRun: async (runId: string): Promise<void> => {
            const db = await open();
            return new Promise((resolve, reject) => {
                const tx = db.transaction(STORE, 'readwrite');
                tx.objectStore(STORE).delete(runId);
                tx.oncomplete = () => resolve();
                tx.onerror = () => reject(tx.error);
            });
        },
        // FE2 fix (2026-04-25): pełny clear przy logout (shared device leak protection)
        clearAll: (): Promise<void> => new Promise((resolve) => {
            try {
                const req = indexedDB.deleteDatabase(DB_NAME);
                req.onsuccess = () => resolve();
                req.onerror = () => resolve();
                req.onblocked = () => resolve();
            } catch { resolve(); }
        }),
    };
})();

// --- BRAMKA KOMPLETNOŚCI LINII (29.08.2026) ---
// Reguły `requires: 'transactions'` przepuszczała dotąd dowolna niepusta tablica.
// Na audycie Car Spa konto 2400 miało w liniach 1,53 mln mniej niż ruch w
// saldobalanse, więc „reguła przeszła" mogło znaczyć „policzono na kawałku
// księgi", a nikt tego nie widział. Ta bramka porównuje sumę linii per konto
// z ruchem z TB i mówi, kiedy linii brakuje.

export interface TxCoverageGap {
    accNum: number;
    fromLines: number;
    fromTb: number;
    missing: number;
}

export interface TxCoverage {
    ok: boolean;
    checkedAccounts: number;
    gaps: TxCoverageGap[];
    totalMissing: number;
}

// Konto z ruchem poniżej tej kwoty to szum, nie sygnał o urwanym harveście.
const COVERAGE_MIN_MOVEMENT = 1000;

export function checkTransactionCoverage(
    saldo: AccountRow[],
    resultat: ResultRow[],
    transactions: TransactionRow[],
): TxCoverage {
    if (!transactions || transactions.length === 0) {
        return { ok: true, checkedAccounts: 0, gaps: [], totalMissing: 0 };
    }

    // Suma ZE ZNAKIEM: linie znoszące się na koncie mają dać zero i tak ma być.
    const perAccount = new Map<number, number>();
    for (const tx of transactions) {
        const acc = Number(tx?.accountNo);
        if (!Number.isFinite(acc)) continue;
        perAccount.set(acc, (perAccount.get(acc) || 0) + (Number(tx?.amount) || 0));
    }

    // Oczekiwany ruch okresu: bilans z `change`, wynik z `sum`.
    const expected = new Map<number, number>();
    for (const row of saldo || []) {
        const acc = Number(row?.accNum);
        if (Number.isFinite(acc)) expected.set(acc, Number(row?.change) || 0);
    }
    for (const row of resultat || []) {
        const acc = Number(row?.accNum);
        if (Number.isFinite(acc) && !expected.has(acc)) expected.set(acc, Number(row?.sum) || 0);
    }

    const gaps: TxCoverageGap[] = [];
    let checkedAccounts = 0;

    for (const [accNum, tbValue] of expected) {
        const fromTb = Math.abs(tbValue);
        if (fromTb < COVERAGE_MIN_MOVEMENT) continue;
        checkedAccounts++;
        const fromLines = Math.abs(perAccount.get(accNum) || 0);
        // Porównujemy wartości bezwzględne, bo konwencja znaku różni się między
        // systemami (`costsArePositive`). Brak danych poznajemy po tym, że linii
        // jest MNIEJ niż ruchu w TB. Więcej linii niż ruchu znaczy szerszy zakres
        // dat (24SO potrafi zignorować filtr daty), a to nie jest brak danych,
        // więc tego przypadku nie zgłaszamy.
        const tolerance = Math.max(100, fromTb * 0.01);
        if (fromLines < fromTb - tolerance) {
            gaps.push({ accNum, fromLines, fromTb, missing: fromTb - fromLines });
        }
    }

    gaps.sort((a, b) => b.missing - a.missing);
    return {
        ok: gaps.length === 0,
        checkedAccounts,
        gaps,
        totalMissing: gaps.reduce((sum, g) => sum + g.missing, 0),
    };
}

// Expose to window for backward compatibility
Object.assign(window as any, {
    ExcelProcessor, MONTH_SHORT, Helpers, ApiTransform, AuditDB, checkTransactionCoverage,
});
